/**
 * Hook for creating a We Met connection request (v2).
 *
 * Replaces the legacy multi-place fan-out with a single-place v2 POST.
 * The v2 endpoint creates a bidirectional `WeMetConnection` (pending →
 * confirm/decline lifecycle) instead of a flat `Interaction` row.
 *
 * Two entry points share ONE mutation (the cache/toast invariants live in a
 * single place):
 * - `useWeMetUserFanout` — the generalized core. The counterpart user travels
 *   in the mutation variables, so one hook instance can fan out over many
 *   USERS at one place (the roster We Met drawer) exactly like the profile
 *   drawer fans a single user out to a place.
 * - `useWeMetFanout` — the original single-user API (profile drawer), now a
 *   thin adapter that binds the user at hook level and keeps its public
 *   signature unchanged.
 *
 * TECH-295
 */

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { createWeMetConnectionV2 } from '@api-client/api-client';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useToast } from '@hooks/use-toast';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { weMetConnectionsKey } from '@lib/cache/questions';
import {
  WeMetConnectionStatus,
  toWeMetConnectionCreate,
  type WeMetConnectionRead,
  type WeMetPlace,
} from '@models/api/we-met-connection';
import { getErrorDetail } from '@utils/errorDetection';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

// ── Public types ────────────────────────────────────────────────────────────

/**
 * The single selected place where the users met, plus its display name.
 * Built on the {@link WeMetPlace} discriminated union so event/space stay
 * mutually exclusive — the request body is derived via `toWeMetConnectionCreate`.
 */
export type SelectedPlace = WeMetPlace & { name: string };

/** One create request: a counterpart user and the place you met. */
export interface WeMetFanoutSubmission {
  place: SelectedPlace;
  /** The user being connected to. */
  otherUserId: string;
  /** First name (or full-name fallback) used in the optimistic row and toasts. */
  otherUserFirstName: string;
}

/**
 * How a submission ended, as classified by the mutation itself:
 * - `landed` — 201 (pending or auto-confirmed) OR an idempotent 409 (the
 *   connection already exists, which is the desired outcome).
 * - `failed` — a real error: optimistic row rolled back, destructive toast shown.
 */
export type WeMetFanoutOutcome = 'landed' | 'failed';

export interface UseWeMetUserFanoutInput {
  /** The viewer's user id — written into the optimistic row's `user_id`. */
  currentUserId: string;
  /** Fired once per submission after the mutation classified its outcome. */
  onResult?: (outcome: WeMetFanoutOutcome, submission: WeMetFanoutSubmission) => void;
}

export interface UseWeMetUserFanoutResult {
  /** Fire one We Met v2 POST. Call once per selected user to fan out a batch. */
  submitTo: (submission: WeMetFanoutSubmission) => void;
  /** Latest-call pending flag; batch surfaces should track settlement via `onResult`. */
  isPending: boolean;
}

export interface UseWeMetFanoutInput {
  /** The viewer's user id — written into the optimistic row's `user_id`. */
  currentUserId: string;
  /** The profile being connected to. */
  otherUserId: string;
  otherUserFirstName: string;
  /** Fired after the connection request landed (201, auto-confirmed, or idempotent 409). */
  onSuccess?: () => void;
}

export interface UseWeMetFanoutResult {
  /** Fire a single We Met v2 POST for the selected place. */
  submit: (place: SelectedPlace) => void;
  isPending: boolean;
}

/** Context carried from onMutate → onSuccess/onError for reconcile + rollback. */
interface FanoutContext {
  previousData: WeMetConnectionRead[] | undefined;
  tempId: string;
}

const HTTP_CONFLICT = 409;

/**
 * We Met connection creation with an optimistic cache update, parameterized
 * per submission so callers can fan out over users.
 *
 * Owns the full mutation lifecycle so the UI wrappers (`WeMetSpacesDrawer`,
 * `WeMetRosterDrawer`) stay declarative — they just pass submissions and
 * callbacks. The counterpart user rides in the mutation variables (not the
 * hook closure), which both enables the user fan-out and keeps the callbacks
 * stale-closure-free.
 *
 * Cache strategy (mirrors `useRememberUserToggle`):
 * - `onMutate` optimistically inserts a `pending_outgoing` row into
 *   `weMetConnectionsKey()` (the unfiltered key — every consumer reads it; the
 *   query stores a bare `WeMetConnectionRead[]`, not an Axios envelope). This
 *   flips the action-row pill to "Pending" instantly via `useWeMetConnectionWith`.
 * - `onSuccess` swaps the temp row for the server row (auto-confirm flips it to
 *   `confirmed`). `onError` rolls back to the snapshot.
 * - `onSettled` invalidates `weMetConnectionsKey()` to reconcile to server truth.
 *
 * 409 handling: backend returns "A We Met connection with this user already
 * exists" — rolled back and treated as an idempotent success with a gentle toast.
 */
export function useWeMetUserFanout({
  currentUserId,
  onResult,
}: UseWeMetUserFanoutInput): UseWeMetUserFanoutResult {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { trackEvent } = useAmplitudeTracking();

  const { mutate, isPending } = useMutation<
    WeMetConnectionRead,
    Error,
    WeMetFanoutSubmission,
    FanoutContext
  >({
    mutationFn: async ({ place, otherUserId }) => {
      const response = await createWeMetConnectionV2(toWeMetConnectionCreate(otherUserId, place));
      return response.data;
    },

    onMutate: async ({ place, otherUserId, otherUserFirstName }) => {
      await queryClient.cancelQueries({ queryKey: weMetConnectionsKey() });
      const previousData = queryClient.getQueryData<WeMetConnectionRead[]>(weMetConnectionsKey());

      const tempId = `optimistic-we-met-${crypto.randomUUID()}`;
      const now = new Date().toISOString();
      const optimisticRow: WeMetConnectionRead = {
        id: tempId,
        user_id: currentUserId,
        connected_user_id: otherUserId,
        event_id: place.kind === 'event' ? place.id : null,
        space_id: place.kind === 'space' ? place.id : null,
        status: WeMetConnectionStatus.NOT_CONFIRMED,
        is_initiator: true,
        created_at: now,
        confirmed_at: null,
        user: { id: otherUserId, name: otherUserFirstName, profile_picture: null, slug: '' },
        event_name: place.kind === 'event' ? place.name : null,
        space_name: place.kind === 'space' ? place.name : null,
      };
      queryClient.setQueryData<WeMetConnectionRead[]>(weMetConnectionsKey(), old =>
        old ? [optimisticRow, ...old] : [optimisticRow],
      );

      return { previousData, tempId };
    },

    onSuccess: (connection, submission, context) => {
      const { place, otherUserId, otherUserFirstName } = submission;
      const isAutoConfirmed = connection.status === WeMetConnectionStatus.CONFIRMED;

      // Reconcile: replace the optimistic temp row with the real server row,
      // deduping in case the server row id already landed via a refetch. The
      // create endpoint enriches the row server-side (counterpart `user` + venue
      // name — see backend `_enrich_connection`), so the server row is
      // authoritative and replaces the optimistic one directly. No client-side
      // fall-back merge: it was removed in #920 because backend #537 enriches
      // every v2 mutation response, and re-adding one would resurrect the
      // blank-card ambiguity #921 killed.
      queryClient.setQueryData<WeMetConnectionRead[]>(weMetConnectionsKey(), old => {
        if (!old) return [connection];
        const withoutTempOrDup = old.filter(c => c.id !== context.tempId && c.id !== connection.id);
        return [connection, ...withoutTempOrDup];
      });

      trackEvent(ANALYTICS_EVENTS.we_met_request_sent, {
        connection_id: connection.id,
        target_user_id: otherUserId,
        place_kind: place.kind,
        place_id: place.id,
        place_name: place.name,
        auto_confirmed: isAutoConfirmed,
      });
      toast({
        title: isAutoConfirmed ? 'Connected!' : 'Request sent',
        description: isAutoConfirmed
          ? `You and ${otherUserFirstName} are connected at ${place.name}.`
          : `${otherUserFirstName} will be notified to confirm at ${place.name}.`,
        variant: 'success',
      });
      onResult?.('landed', submission);
    },

    onError: (error, submission, context) => {
      const { place, otherUserId, otherUserFirstName } = submission;
      // Roll back the optimistic insert first (restore snapshot, or drop the
      // entry entirely if the cache was unseeded before this mutation).
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(weMetConnectionsKey(), context.previousData);
      } else {
        queryClient.removeQueries({ queryKey: weMetConnectionsKey(), exact: true });
      }

      if (isAxiosError(error) && error.response?.status === HTTP_CONFLICT) {
        // Connection already exists — idempotently the desired outcome. The
        // onSettled invalidation refetches the real row.
        toast({
          title: 'Already connected',
          description: `You already have a connection with ${otherUserFirstName}.`,
        });
        onResult?.('landed', submission);
        return;
      }
      logger.error('[wemet-v2] connection creation failed', {
        error: sanitizeError(error),
        otherUserId,
        placeKind: place.kind,
        placeId: place.id,
      });
      const detail = getErrorDetail(error);
      toast({
        title: "Couldn't send request",
        description: detail ?? 'Try again in a moment.',
        variant: 'destructive',
      });
      onResult?.('failed', submission);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: weMetConnectionsKey() });
    },
  });

  // `mutate` is referentially stable across renders — see the note in
  // `useWeMetFanout` below about @tanstack/query/no-unstable-deps.
  const submitTo = useCallback(
    (submission: WeMetFanoutSubmission) => {
      mutate(submission);
    },
    [mutate],
  );

  return { submitTo, isPending };
}

/**
 * Single-user We Met creation — the original profile-drawer API, delegating
 * to `useWeMetUserFanout` so both surfaces share the one mutation.
 */
export function useWeMetFanout({
  currentUserId,
  otherUserId,
  otherUserFirstName,
  onSuccess,
}: UseWeMetFanoutInput): UseWeMetFanoutResult {
  const { submitTo, isPending } = useWeMetUserFanout({
    currentUserId,
    onResult: outcome => {
      if (outcome === 'landed') onSuccess?.();
    },
  });

  // `submitTo` is memoized on the stable `mutate`; `isPending` is a primitive.
  // Depending on the whole mutation object (a fresh ref every render) would
  // defeat the memo — see TanStack's @tanstack/query/no-unstable-deps rule.
  const submit = useCallback(
    (place: SelectedPlace) => {
      if (isPending) return;
      submitTo({ place, otherUserId, otherUserFirstName });
    },
    [submitTo, isPending, otherUserId, otherUserFirstName],
  );

  return { submit, isPending };
}
